// frontend/src/pages/cargo/CargoFlow.js
import { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CreateCover from './CreateCover';
import QuotationsList from './QuotationsList';
import AcceptanceStep from './AcceptanceStep';
import PaymentStep from './PaymentStep';
import CertificateStep from './CertificateStep';
import PolicyScheduleStep from './PolicyScheduleStep';

const STEPS = [
    { label: 'New Cover', icon: '📝', short: 'Cover', description: 'Create your marine cover', color: 'from-blue-500 to-blue-600' },
    { label: 'Quotations', icon: '📋', short: 'Quotes', description: 'Review insurance quotes', color: 'from-purple-500 to-purple-600' },
    { label: 'Acceptance', icon: '✅', short: 'Accept', description: 'Confirm and accept', color: 'from-amber-500 to-amber-600' },
    { label: 'Payment', icon: '💳', short: 'Pay', description: 'Complete payment', color: 'from-green-500 to-green-600' },
    { label: 'Certificate', icon: '📄', short: 'Cert', description: 'View & download certificate', color: 'from-rose-500 to-rose-600' },
    { label: 'Policy Schedule', icon: '📘', short: 'Policy', description: 'Issued policy schedule', color: 'from-indigo-500 to-indigo-600' },
];

const FLOW_STORAGE_KEY = 'cargo_flow_state';

const EMPTY_FLOW_DATA = {
    coverData: null,
    idfData: null,
    selectedQuote: null,
    acceptance: null,
    payment: null,
    certificate: null,
    schedule: null,
    coverFormData: null,
};

export default function CargoFlow() {
    const location = useLocation();
    const navigate = useNavigate();
    const isInitialMount = useRef(true);

    const loadSavedState = useCallback(() => {
        try {
            const saved = localStorage.getItem(FLOW_STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                const savedTime = parsed._timestamp || 0;
                const isExpired = Date.now() - savedTime > 24 * 60 * 60 * 1000;
                if (!isExpired) return parsed;
            }
        } catch (e) {
            console.warn('[CargoFlow] Failed to load saved state:', e);
        }
        return null;
    }, []);

    const prefillData = location.state?.quoteData || null;
    const prefillIdfData = location.state?.idfData || null;
    const fromQuote = location.state?.fromQuote || false;

    const getInitialState = useCallback(() => {
        const savedState = loadSavedState();
        if (savedState) {
            return {
                currentStep: savedState.currentStep || 0,
                completedSteps: savedState.completedSteps || [],
                flowData: { ...EMPTY_FLOW_DATA, ...(savedState.flowData || {}) },
                hasDraft: true,
                showDraftBanner: true,
            };
        }
        return {
            currentStep: 0,
            completedSteps: [],
            flowData: { ...EMPTY_FLOW_DATA, idfData: prefillIdfData || null },
            hasDraft: false,
            showDraftBanner: false,
        };
    }, [loadSavedState, prefillIdfData]);

    const initialState = getInitialState();
    const [currentStep, setCurrentStep] = useState(initialState.currentStep);
    const [completedSteps, setCompletedSteps] = useState(initialState.completedSteps);
    const [flowData, setFlowData] = useState(initialState.flowData);
    const [direction, setDirection] = useState('forward');
    const [hasDraft, setHasDraft] = useState(initialState.hasDraft);
    const [showDraftBanner, setShowDraftBanner] = useState(initialState.showDraftBanner);

    const saveState = useCallback((data) => {
        try {
            const stateToSave = {
                currentStep: data.currentStep,
                completedSteps: data.completedSteps,
                flowData: data.flowData,
                _timestamp: Date.now(),
            };
            localStorage.setItem(FLOW_STORAGE_KEY, JSON.stringify(stateToSave));
        } catch (e) {
            console.warn('[CargoFlow] Failed to save state:', e);
        }
    }, []);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        saveState({ currentStep, completedSteps, flowData });
        if (flowData.coverData || flowData.idfData || flowData.selectedQuote) {
            setHasDraft(true);
        }
    }, [currentStep, completedSteps, flowData, saveState]);

    useEffect(() => {
        if (fromQuote && prefillData) {
            localStorage.removeItem(FLOW_STORAGE_KEY);
            setCurrentStep(0);
            setCompletedSteps([]);
            setFlowData({ ...EMPTY_FLOW_DATA, idfData: prefillIdfData || null });
            setHasDraft(false);
            setShowDraftBanner(false);
        }
    }, [fromQuote, prefillData, prefillIdfData]);

    const goToStep = useCallback((stepIndex) => {
        setDirection(stepIndex > currentStep ? 'forward' : 'backward');
        setCurrentStep(stepIndex);
        saveState({ currentStep: stepIndex, completedSteps, flowData });
    }, [currentStep, completedSteps, flowData, saveState]);

    // =========================================================
    // STEP HANDLERS
    // =========================================================
    const handleCoverComplete = (coverAndIDF) => {
        const coverFormData = coverAndIDF.coverFormData || coverAndIDF.cover || null;
        let idfData = coverAndIDF.idfData || flowData.idfData || prefillIdfData || null;

        if (!idfData && coverFormData) {
            idfData = {
                body: {
                    idf_number: coverFormData.idf_number || '',
                    ucr_number: coverFormData.ucr_number || '',
                    origin_country: coverFormData.voyage_from_country || '',
                    mode_of_transport: coverFormData.mode_of_transport || 'Sea',
                    consignee: [{
                        destination_country: coverFormData.voyage_to_country || 'Kenya',
                        email: coverFormData.consignee_email || '',
                        phone_number: coverFormData.consignee_phone || '',
                        county: coverFormData.consignee_county || '',
                        city: coverFormData.consignee_city || '',
                        postal_code: coverFormData.consignee_postal_code || '',
                        postal_address: coverFormData.consignee_address || '',
                        country_of_origin: coverFormData.consignee_country_of_origin || '',
                    }],
                    complimentaries: { idf_invoice_num: coverFormData.commercial_invoice_number || '' },
                    delivery_place: coverFormData.port_of_discharge || '',
                    items: coverFormData.nature_of_goods ? [{ tariff_goods_desc: coverFormData.nature_of_goods }] : [],
                },
            };
        }

        const newFlowData = {
            ...flowData,
            coverData: coverAndIDF.cover,
            idfData,
            coverFormData,
            vessel_name: coverFormData?.vessel_name || '',
            loading_at: coverFormData?.loading_at || '',
            port_of_discharge: coverFormData?.port_of_discharge || '',
            open_cover_number: coverFormData?.open_cover_number || '',
            voyage_from_country: coverFormData?.voyage_from_country || '',
            voyage_to_country: coverFormData?.voyage_to_country || 'Kenya',
            mode_of_transport: coverFormData?.mode_of_transport || 'Sea',
            consignee_email: coverFormData?.consignee_email || '',
            consignee_phone: coverFormData?.consignee_phone || '',
            consignee_country_of_origin: coverFormData?.consignee_country_of_origin || '',
            consignee_county: coverFormData?.consignee_county || '',
            consignee_city: coverFormData?.consignee_city || '',
            consignee_postal_code: coverFormData?.consignee_postal_code || '',
            consignee_address: coverFormData?.consignee_address || '',
        };

        const newCompletedSteps = [...new Set([...completedSteps, 0])];
        setCompletedSteps(newCompletedSteps);
        setFlowData(newFlowData);
        setDirection('forward');
        setCurrentStep(1);
        saveState({ currentStep: 1, completedSteps: newCompletedSteps, flowData: newFlowData });
    };

    const handleSelectQuote = (quote) => {
        const newCompletedSteps = [...new Set([...completedSteps, 1])];
        setCompletedSteps(newCompletedSteps);
        setFlowData((prev) => {
            const newFlowData = { ...prev, selectedQuote: quote };
            saveState({ currentStep: 2, completedSteps: newCompletedSteps, flowData: newFlowData });
            return newFlowData;
        });
        setDirection('forward');
        setCurrentStep(2);
    };

    const handleAcceptanceComplete = (acceptance) => {
        const acceptanceWithData = {
            ...acceptance,
            id: acceptance?.id || acceptance?.body?.id || acceptance?.acceptance_id || Date.now(),
            quote_number: acceptance?.quote_number || acceptance?.body?.quoteNumber || 'N/A',
            acceptance_response_json: acceptance?.acceptance_response_json || acceptance,
            idf_number: flowData?.idfData?.body?.idf_number || flowData?.idfData?.body?.idfNumber || acceptance?.idf_number || '',
            coverFormData: flowData?.coverFormData || {},
            consignee: flowData?.coverFormData || {},
        };

        const newCompletedSteps = [...new Set([...completedSteps, 2])];
        setCompletedSteps(newCompletedSteps);
        setFlowData((prev) => {
            const newFlowData = { ...prev, acceptance: acceptanceWithData };
            saveState({ currentStep: 3, completedSteps: newCompletedSteps, flowData: newFlowData });
            return newFlowData;
        });
        setDirection('forward');
        setCurrentStep(3);
    };

    const handlePaymentComplete = (payment) => {
        const newCompletedSteps = [...new Set([...completedSteps, 3])];
        setCompletedSteps(newCompletedSteps);
        setFlowData((prev) => {
            const newFlowData = { ...prev, payment };
            saveState({ currentStep: 4, completedSteps: newCompletedSteps, flowData: newFlowData });
            return newFlowData;
        });
        setDirection('forward');
        setCurrentStep(4);
    };

    // Certificate complete → mark step 4 done but DO NOT auto-advance.
    // The user must click "Continue to Policy Schedule" in CertificateStep.
    const handleCertificateComplete = (certificate) => {
        const newCompletedSteps = [...new Set([...completedSteps, 4])];
        setCompletedSteps(newCompletedSteps);
        setFlowData((prev) => {
            const newFlowData = { ...prev, certificate };
            saveState({ currentStep: 4, completedSteps: newCompletedSteps, flowData: newFlowData });
            return newFlowData;
        });
        // Stay on step 4 — CertificateStep owns the navigation to step 5
    };

    // Called by CertificateStep when the user clicks "Continue to Policy Schedule"
    const handleProceedToSchedule = () => {
        setDirection('forward');
        setCurrentStep(5);
        saveState({ currentStep: 5, completedSteps, flowData });
    };

    // Policy Schedule complete → clear draft, mark step 5 done
    const handleScheduleComplete = (schedule) => {
        const newCompletedSteps = [...new Set([...completedSteps, 5])];
        setCompletedSteps(newCompletedSteps);
        setFlowData((prev) => {
            const newFlowData = { ...prev, schedule };
            saveState({ currentStep: 5, completedSteps: newCompletedSteps, flowData: newFlowData });
            return newFlowData;
        });
        localStorage.removeItem(FLOW_STORAGE_KEY);
        setHasDraft(false);
        setShowDraftBanner(false);
    };

    const handleClearDraft = () => {
        if (window.confirm('Are you sure you want to clear your draft? All unsaved progress will be lost.')) {
            localStorage.removeItem(FLOW_STORAGE_KEY);
            setCurrentStep(0);
            setCompletedSteps([]);
            setFlowData({ ...EMPTY_FLOW_DATA });
            setHasDraft(false);
            setShowDraftBanner(false);
        }
    };

    const handleStartNew = () => {
        if (hasDraft && !window.confirm('You have a draft in progress. Starting a new certificate will clear your draft. Continue?')) {
            return;
        }
        localStorage.removeItem(FLOW_STORAGE_KEY);
        setCurrentStep(0);
        setCompletedSteps([]);
        setFlowData({ ...EMPTY_FLOW_DATA });
        setHasDraft(false);
        setShowDraftBanner(false);
    };

    const progressPercentage = completedSteps.length > 0
        ? Math.round((completedSteps.length / STEPS.length) * 100)
        : 0;

    // =========================================================
    // RENDER STEP
    // =========================================================
    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return (
                    <CreateCover
                        onComplete={handleCoverComplete}
                        prefillData={prefillData}
                        prefillIdfData={prefillIdfData}
                        initialFormData={flowData.coverFormData}
                    />
                );

            case 1:
                if (!flowData.idfData) {
                    return (
                        <div className="max-w-2xl mx-auto text-center py-16 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700">
                            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-amber-50 dark:bg-amber-900/20 mb-4">
                                <svg className="w-10 h-10 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white">No IDF Data Available</h3>
                            <p className="text-sm text-gray-500 dark:text-slate-400 mt-2 max-w-sm mx-auto">
                                Please complete the IDF lookup in the cover creation step first.
                            </p>
                            <button
                                onClick={() => goToStep(0)}
                                className="mt-4 inline-flex items-center gap-2 px-6 py-2.5 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors shadow-md hover:shadow-lg"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Go Back to Cover
                            </button>
                        </div>
                    );
                }
                return <QuotationsList idfData={flowData.idfData} onSelect={handleSelectQuote} />;

            case 2:
                return flowData.selectedQuote && flowData.idfData ? (
                    <AcceptanceStep
                        quote={flowData.selectedQuote}
                        idfData={flowData.idfData}
                        onComplete={handleAcceptanceComplete}
                    />
                ) : null;

            case 3:
                return flowData.acceptance ? (
                    <PaymentStep
                        acceptance={flowData.acceptance}
                        coverFormData={flowData.coverFormData}
                        onComplete={handlePaymentComplete}
                    />
                ) : null;

            case 4:
                return flowData.acceptance ? (
                    <CertificateStep
                        acceptance={flowData.acceptance}
                        onComplete={handleCertificateComplete}
                        onProceedToSchedule={handleProceedToSchedule}
                    />
                ) : null;

            case 5:
                return flowData.acceptance && flowData.certificate ? (
                    <PolicyScheduleStep
                        acceptance={flowData.acceptance}
                        certificate={flowData.certificate}
                        onComplete={handleScheduleComplete}
                    />
                ) : null;

            default:
                return null;
        }
    };

    // =========================================================
    // RENDER
    // =========================================================
    return (
        <div className="max-w-6xl mx-auto px-4 py-8 animate-fadeIn">

            {/* ============ DRAFT BANNER ============ */}
            {showDraftBanner && hasDraft && (
                <div className="mb-6 bg-gradient-to-r from-amber-50 to-amber-100/50 dark:from-amber-900/20 dark:to-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-2xl p-4 flex items-center justify-between flex-wrap gap-4 shadow-lg shadow-amber-500/10">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                            <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                                💾 Draft in Progress — Step {currentStep + 1}: {STEPS[currentStep]?.label}
                            </p>
                            <p className="text-xs text-amber-600 dark:text-amber-400">
                                Your progress is automatically saved. Continue where you left off.
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={handleClearDraft}
                            className="px-4 py-2 text-sm text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-800/30 rounded-lg transition-colors"
                        >
                            🗑️ Clear Draft
                        </button>
                        <button
                            onClick={() => setShowDraftBanner(false)}
                            className="px-4 py-2 text-sm bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-all shadow-md hover:shadow-lg"
                        >
                            ✕ Dismiss
                        </button>
                    </div>
                </div>
            )}

            {/* ============ HEADER ============ */}
            <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#2d5232] to-[#3a663f] shadow-2xl shadow-[#3a663f]/25 mb-4 relative">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/10 to-transparent"></div>
                    <svg className="w-10 h-10 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                </div>
                <h1 className="text-4xl font-extrabold text-gray-800 dark:text-white tracking-tight">
                    Marine Cargo Certificate
                </h1>
                <p className="text-gray-500 dark:text-slate-400 mt-2 max-w-lg mx-auto">
                    Complete each step to generate your insurance certificate and policy schedule
                </p>

                {/* Status badges */}
                <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-brand-500/10 dark:bg-brand-500/20 text-brand-600 dark:text-green-400 text-xs font-medium rounded-full border border-brand-500/20">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {completedSteps.length} of {STEPS.length} Completed
                    </span>
                    {flowData.idfData && (
                        <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 text-xs font-medium rounded-full">
                            🆔 IDF: {flowData.idfData?.body?.idf_number || flowData.idfData?.body?.idfNumber || 'N/A'}
                        </span>
                    )}
                    {fromQuote && prefillData && (
                        <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-medium rounded-full border border-blue-200 dark:border-blue-800">
                            🔄 Converted from Quote #{prefillData.quote_number}
                        </span>
                    )}
                    {hasDraft && (
                        <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-xs font-medium rounded-full border border-amber-200 dark:border-amber-800">
                            💾 Draft Saved
                        </span>
                    )}
                </div>

                {/* Progress bar */}
                <div className="max-w-md mx-auto mt-6">
                    <div className="flex items-center justify-between text-xs text-gray-400 dark:text-slate-500 mb-1.5">
                        <span>Progress</span>
                        <span className="font-medium text-brand-600 dark:text-green-400">{progressPercentage}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-[#2d5232] to-[#3a663f] rounded-full transition-all duration-700 ease-out"
                            style={{ width: `${progressPercentage}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* ============ PROGRESS TRACKER ============ */}
            <div className="mb-12">
                {/* Desktop */}
                <div className="hidden md:flex items-center justify-between relative px-4">
                    <div className="absolute top-1/2 left-10 right-10 h-1 bg-gray-200 dark:bg-slate-700 rounded-full -translate-y-1/2 z-0" />
                    <div
                        className="absolute top-1/2 left-10 h-1 bg-gradient-to-r from-[#2d5232] to-[#3a663f] rounded-full -translate-y-1/2 z-0 transition-all duration-700 ease-out"
                        style={{
                            width: `calc(${(currentStep / (STEPS.length - 1)) * 100}% - 1rem)`,
                            left: '2.5rem',
                        }}
                    />
                    {STEPS.map((step, idx) => {
                        const isCompleted = idx < currentStep;
                        const isActive = idx === currentStep;
                        const isUpcoming = idx > currentStep;

                        return (
                            <button
                                key={step.label}
                                onClick={() => isCompleted && goToStep(idx)}
                                disabled={isUpcoming && !completedSteps.includes(idx)}
                                className="relative z-10 flex flex-col items-center group"
                            >
                                <div
                                    className={`
                                        w-16 h-16 rounded-full flex items-center justify-center border-2 transition-all duration-300
                                        ${isCompleted
                                            ? 'bg-brand-600 border-brand-600 text-white cursor-pointer hover:scale-110 shadow-lg shadow-brand-600/30'
                                            : isActive
                                                ? 'border-brand-600 bg-white dark:bg-slate-800 text-brand-600 dark:text-green-400 shadow-2xl shadow-brand-600/30 scale-110 ring-4 ring-brand-500/10'
                                                : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-400 dark:text-slate-500'
                                        }
                                    `}
                                >
                                    {isCompleted ? (
                                        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                        </svg>
                                    ) : isActive ? (
                                        <div className="relative">
                                            <span className="text-2xl">{step.icon}</span>
                                            <div className="absolute -bottom-1 -right-1 w-3 h-3">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-500/40"></span>
                                                <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-600"></span>
                                            </div>
                                        </div>
                                    ) : (
                                        <span className="text-2xl opacity-60">{step.icon}</span>
                                    )}
                                </div>
                                <span
                                    className={`mt-3 text-sm font-medium transition-colors ${isActive
                                            ? 'text-brand-600 dark:text-green-400 font-bold'
                                            : isCompleted
                                                ? 'text-brand-600 dark:text-green-400'
                                                : 'text-gray-400 dark:text-slate-500'
                                        }`}
                                >
                                    {step.label}
                                </span>
                                <span className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5">
                                    {step.description}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* Mobile */}
                <div className="md:hidden flex items-center justify-between bg-white dark:bg-slate-800 rounded-2xl p-4 shadow-lg border border-gray-100 dark:border-slate-700">
                    {STEPS.map((step, idx) => (
                        <div key={step.label} className="flex flex-col items-center">
                            <div
                                className={`
                                    w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold transition-all
                                    ${idx <= currentStep
                                        ? 'bg-brand-600 text-white shadow-md shadow-brand-600/20'
                                        : 'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                                    }
                                `}
                            >
                                {idx < currentStep ? (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : idx === currentStep ? (
                                    <span className="text-base">{step.icon}</span>
                                ) : (
                                    <span className="text-xs">{idx + 1}</span>
                                )}
                            </div>
                            <span
                                className={`text-[10px] mt-1 transition-colors ${idx === currentStep
                                        ? 'text-brand-600 dark:text-green-400 font-bold'
                                        : 'text-gray-500 dark:text-slate-400'
                                    }`}
                            >
                                {step.short}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* ============ STEP CONTENT ============ */}
            <div className="bg-white/50 dark:bg-slate-800/50 rounded-3xl backdrop-blur-sm p-1">
                <div
                    className={`transition-all duration-500 transform ${direction === 'forward' ? 'animate-slideInRight' : 'animate-slideInLeft'
                        }`}
                >
                    {renderStep()}
                </div>
            </div>

            {/* ============ NAVIGATION FOOTER ============ */}
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                    {currentStep > 0 && currentStep !== 4 && (
                        <button
                            onClick={() => goToStep(currentStep - 1)}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl border border-gray-200 dark:border-slate-600 transition-all shadow-sm hover:shadow-md"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            Back to {STEPS[currentStep - 1]?.label}
                        </button>
                    )}
                    {currentStep === 4 && (
                        <span className="text-xs text-gray-400 dark:text-slate-500 italic">
                            Review your certificate above, then continue when ready
                        </span>
                    )}
                    {currentStep === 0 && (
                        <button
                            onClick={handleStartNew}
                            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Start Fresh
                        </button>
                    )}
                </div>

                {currentStep === STEPS.length - 1 && completedSteps.length === STEPS.length && (
                    <button
                        onClick={handleStartNew}
                        className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white font-medium rounded-xl shadow-lg shadow-brand-600/20 hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Start New Certificate
                    </button>
                )}
            </div>
        </div>
    );
}